/* eslint-disable max-lines-per-function */
import React, { useState } from "react";
import { gql } from "graphql-tag";
import { ApolloProvider as ApolloHookProvider, useMutation } from "@apollo/client";
import { DialogContent, Grid, Typography } from "@material-ui/core";
import { Modal } from "components/haven-design";
import { useSnackbar } from "components/haven-design/snackbar";
import { TemplateSelector } from "components/send-messages/template-selector";
import { TemplateForm, SubmitValues } from "components/send-messages";
import { Viewer } from "components/auth/types";
import { apolloClient } from "data/graphql";
import {
  ElasticQuery,
  SendMessages,
  SendMessagesVariables,
  getTemplateGroup_templateGroup as Template,
} from "types/gql-types";

import { calculateMessageValues } from "utils/template";
import logger from "services/logger";
import { TableResult, useInterestsTable } from "../interests-table/table-context";
import { formatFilters, useSidebarFilters } from "../sidebar-filters";
import { renderRecipients } from "./utils";


const SEND_MESSAGE = gql`
  mutation SendMessages(
    $interestListIds: [ID!]!
    $interestIds: [ID!]
    $elasticQuery: ElasticQuery
    $pageFrom: Int
    $pageTo: Int
    $emailEnabled: Boolean!
    $emailRespondToEnabled: Boolean
    $emailBody: String!
    $emailSubject: String!
    $letterEnabled: Boolean!
    $letterBody: String!
    $smsEnabled: Boolean!
    $smsBody: String!
    $emailBccAddress: String
    $smsPropertyUseCaseId: ID
  ) {
    sendMessages(
      interestListIds: $interestListIds
      interestIds: $interestIds
      elasticQuery: $elasticQuery
      pageFrom: $pageFrom
      pageTo: $pageTo
      emailEnabled: $emailEnabled
      emailBody: $emailBody
      emailRespondToEnabled: $emailRespondToEnabled
      emailSubject: $emailSubject
      letterEnabled: $letterEnabled
      letterBody: $letterBody
      smsEnabled: $smsEnabled
      smsBody: $smsBody
      emailBccAddress: $emailBccAddress
      smsPropertyUseCaseId: $smsPropertyUseCaseId
    )
  }
`;
interface Props {
  isOpen: boolean;
  onRequestClose(): void;
  recipients: TableResult[];
  viewer: Viewer;
  currentListId: string;
}

interface MessageProps {
  interestListIds: [string];
  actionsButton?: boolean;
  closeMenu?: () => void;
  pageFrom: number | null;
  pageTo: number | null;
}

const getVariables = (
  { interestListIds, pageFrom, pageTo }: MessageProps,
  messageParams: SubmitValues,
) => ({
  interestListIds,
  pageFrom,
  pageTo,
  ...messageParams,
});

const getScope = (
  withQuery: boolean,
  elasticQuery: ElasticQuery | null,
  recipients: TableResult[],
) => {
  const visibleInterestIds = recipients.map((recipient) => recipient.id);
  return {
    interestIds: withQuery ? null : visibleInterestIds,
    elasticQuery: (withQuery && elasticQuery) || null,
  };
};


export const MessageModal: React.FC<Props> = (props) => {
  const { openSnackbar } = useSnackbar();
  const [template, setTemplate] = useState<Template | null>(null);
  const { currentListId, isOpen, onRequestClose, recipients, viewer } = props;
  const updateValues = calculateMessageValues(template);
  const [isSending, setIsSending] = useState(false);
  const [sendTemplateMessages] = useMutation<SendMessages, SendMessagesVariables>(SEND_MESSAGE, { refetchQueries: ["getActivityFeed"] });

  const {
    hasSelectedAllInterests,
    selectedPageRange,
    totalSelectedInterests,
  } = useInterestsTable();
  const {
    filterState,
    textFilter,
  } = useSidebarFilters();
  const elasticQuery = {
    filters: formatFilters(filterState),
    textFilter,
  };


  const onSubmit = (messageParams: SubmitValues) => {
    const withQuery = hasSelectedAllInterests || (
      selectedPageRange.from !== null || selectedPageRange.to !== null
    );
    const variables = {
      ...getVariables({
        interestListIds: [currentListId],
        pageFrom: selectedPageRange.from,
        pageTo: selectedPageRange.to,
      }, messageParams),
      ...getScope(withQuery, elasticQuery, recipients),
    };


    setIsSending(true);
    sendTemplateMessages({ variables })
      .then(() => {
        openSnackbar({ message: "Success", status: "success" });
      })
      .catch((err) => {
        openSnackbar({
          message: "Error sending bulk message. Please don't retry in case part of the batch has been sent. Contact support@havenconnect.com",
          status: "error",
          duration: 4000,
        });
        logger.sendError(err);
        throw new Error(err);
      })
      .finally(() => {
        setIsSending(false);
        onRequestClose();
      });
  };


  return (
    <Modal title="Send Messages:" isOpen={isOpen} onRequestClose={onRequestClose} fullScreen>
      <DialogContent>
        <ApolloHookProvider client={apolloClient}>
          <Grid item xs={12}>
            <Typography variant="subtitle1">
              To:&nbsp;
              {renderRecipients(
                recipients,
                // @ts-ignore
                totalSelectedInterests,
                selectedPageRange,
              )}&nbsp;
            </Typography>
          </Grid>
          <TemplateSelector
            onChange={(selectedTemplate: Template | null) => {
              if (selectedTemplate) setTemplate(selectedTemplate);
            }}
            viewer={viewer}
            currentTemplate={template}
          />
          <Grid item xs={12}>
            <TemplateForm
              viewer={viewer}
              isSending={isSending}
              handleSubmit={onSubmit}
              template={template}
              onRequestClose={onRequestClose}
              currentListId={currentListId}
              {...updateValues}
            />
          </Grid>
        </ApolloHookProvider>
      </DialogContent>
    </Modal>
  );
};
